import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { EntityTagCloudComponent } from '@app/common-components/entity-tag-cloud/entity-tag-cloud.component';
import { AnnualLeaseService } from '@app/landowner-admin-shared/services/annual-lease.service';
import { ViewChild, ElementRef } from '@angular/core';
import SeasonLookup from '@app/models/season-lookup';
import SeasonLookupRequest from '@app/models/season-lookup-request';
import SeasonLookupViewModel from '@app/models/season-lookup-view-model';
import TagViewModel from '@app/models/tag-view-model';
import { DateService, ErrorHelperService, FormsService, LogService, NotificationService, UserService, ValueService } from '@app/services';
import { TagService } from '@app/services/tag.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { UserNamePipe } from '../../../common-components/pipes/user-name.pipe';
import { EntityTagCloudComponent as EntityTagCloudComponent_1 } from '../../../common-components/entity-tag-cloud/entity-tag-cloud.component';
import { TagPickerComponent } from '../../../common-components/tag-picker/tag-picker.component';
import { OADateInputComponent } from '../../../common-components/oadate-input/oadate-input.component';
import { FormsModule } from '@angular/forms';
import { OAPopoverComponent } from '../../../common-components/oapopover/oapopover.component';
import { NgIf, NgFor, AsyncPipe, DatePipe } from '@angular/common';

/**
         <season-lookup-form
          [showTitle]="false"
          (cancel)="onCancelSeasonLookup()"
          (add)="onSeasonLookupAdd($event)"
          [value]="selectedSeasonLookup"
          [listingId]="listingId"
        >
        </season-lookup-form>
 */
@Component({
    selector: "season-lookup-form",
    templateUrl: "./season-lookup-form.component.html",
    styleUrls: ["./season-lookup-form.component.less"],
    imports: [NgIf, OAPopoverComponent, FormsModule, OADateInputComponent, NgFor, TagPickerComponent, EntityTagCloudComponent_1, AsyncPipe, DatePipe, UserNamePipe]
})
export class SeasonLookupFormComponent implements OnInit, OnDestroy {
  private readonly _componentName = 'SeasonLookupFormComponent';
  private _selectedState: any;
  private _value: SeasonLookup | SeasonLookupViewModel;
  private _listingId: number;
  private unSubSub$ = new Subject<void>();

  @ViewChild("entityTagCloud") entityTagCloud: EntityTagCloudComponent;

  public tagNames = ['SeasonLookup-VA', 'Deer-TX', 'Deer-SC', 'Deer-NC', 'Deer-VA', 'Waterfowl'];

  /**
   * Subject to control observable completion
   */
  // Add:

  @Input()
  public showTitle = true;

  public get isNew(): boolean {
    if (this.value === undefined) return undefined;

    if (this.value && !this.value.id) {
      return true;
    }

    return false;
  }

  public saving = false;

  @Output()
  public add = new EventEmitter<SeasonLookup>();

  @Output()
  public cancel = new EventEmitter();

  public states: { id: string; name: string }[];

  constructor(
    private valueHelper: ValueService,
    private userService: UserService,
    private dateHelper: DateService,
    private formService: FormsService,
    private log: LogService,
    private notify: NotificationService,
    private annualLeaseService: AnnualLeaseService,
    private errorHelper: ErrorHelperService,
    private tags: TagService
  ) {
    const allStates = this.formService.getStates();

    // console.log(`${this._componentName}.CTOR - allStates`, allStates);

    const recentStates = [
      { id: 'VA', name: 'Virginia' },
      { id: 'NC', name: 'North Carolina' },
      { id: 'SC', name: 'South Carolina' },
      { id: 'PA', name: 'Pennsylvania' },
      { id: 'TX', name: 'Texas' },
    ];

    const specialItems = [{ id: 'Global', name: 'Global' }];

    this.states = specialItems.concat(recentStates.concat(allStates.filter((x) => !recentStates.some((y) => y.id === x.id))));
  }

  ngOnInit() {}

  ngOnDestroy(): void {
    this.unSubSub$.next(void 0);
    this.unSubSub$.complete();
  }

  /**
   * Is verbose logging enabled
   */
  private get verboseLogging(): boolean {
       return this.log.verboseLogging;
  }

  public get value(): SeasonLookup | SeasonLookupViewModel {
    return this._value;
  }

  @Input()
  public set value(v: SeasonLookup | SeasonLookupViewModel) {
    this._value = v;
    console.log(`${this._componentName}.value set v=`, v);

    if (this.valueHelper.getSafeValue(v, 'isGlobal')) {
      this.selectedState = this.states.find((x) => x.id === 'Global');
    } else {
      if (v && this.valueHelper.getSafeValue(v, 'state')) {
        this.selectedState = this.states.find((x) => x.id === this.valueHelper.getSafeValue(v, 'state'));
      } else {
        this.selectedState = null;
      }
    }
  }

  public get selectedState(): any {
    return this._selectedState;
  }

  @Input()
  public set selectedState(v: any) {
    this._selectedState = v;
    console.log(`${this._componentName}.selectedState set v=`, v);
  }

  public get listingId(): number {
    return this._listingId;
  }

  @Input()
  public set listingId(v: number) {
    this._listingId = v;
  }

  /**
   * Saves a new SeasonLookup
   */
  public onSaveClick() {
    console.log(`${this._componentName}.onSaveClick()`);

    var request = {
      entity: this.value,
      listingId: this.listingId,
    } as SeasonLookupRequest;

    if (this.listingId) {
      request.isGlobal = false;
      request.state = null;
    } else {
      if (this.selectedState && this.selectedState.name === 'Global') {
        request.isGlobal = true;
      } else if (this.selectedState && this.selectedState.name !== 'Global') {
        request.isGlobal = false;
        request.state = this.selectedState.id;
      } else {
        request.isGlobal = false;
      }
    }

    this.saving = true;

    if (!this.value.id || this.value.id === 0) {
      this.annualLeaseService
        .createSeasonLookup(request)
        .pipe(takeUntil(this.unSubSub$))
        .subscribe(
          (result) => {
            console.log(`${this._componentName} subscription result=`, result);

            if (result.seasonLookup) {
              this.add.emit(result.seasonLookup);
            } else {
              this.notify.error('Failed to create season lookup.', 'Create Season Lookup Error');
            }
          },
          (err) => {
            console.error(err);

            var msg = this.errorHelper.getErrorMessage(err) || 'A subscription error occured';

            this.saving = false;

            this.notify.error(msg, null);
          },
          () => {
            this.saving = false;
          }
        );
    } else {
      this.annualLeaseService
        .modifySeasonLookup(request)
        .pipe(takeUntil(this.unSubSub$))
        .subscribe(
          (result) => {
            console.log(`${this._componentName} subscription result=`, result);

            if (result) {
              this.add.emit(result);
            } else {
              this.notify.error('Failed to create season lookup.', 'Create Season Lookup Error');
            }
          },
          (err) => {
            console.error(err);

            var msg = this.errorHelper.getErrorMessage(err) || 'A subscription error occured';

            this.saving = false;

            this.notify.error(msg, null);
          },
          () => {
            this.saving = false;
          }
        );
    }
  }

  public onCancelClick() {
    console.log(`${this._componentName}.onCancelClick()`);
    this.saving = false;

    this.cancel.emit();
  }

  public get canSave(): boolean {
    if (this.saving) return false;

    if (!this.value.startDateTime || !this.value.endDateTime) {
      if (this.verboseLogging) {
        console.log(`${this._componentName}.canSave return false - Null/Empty startDateTime/endDateTime`);
      }

      return false;
    }

    if (!this.value.name) {
      if (this.verboseLogging) {
        console.log(`${this._componentName}.canSave return false - Null/Empty name`);
      }

      return false;
    }

    if (!this.listingId) {
      if (!this.selectedState) {
        if (this.verboseLogging) {
          console.log(`${this._componentName}.canSave return false - Null/Empty selectedState`);
        }

        return false;
      }
    }

    if (this.verboseLogging) {
      console.log(`${this._componentName}.canSave return true`);
    }

    return true;
  }

  public onSelectTag(tag: TagViewModel) {
    console.log(`${this._componentName}.onSelectTag() tag=`, tag);

    this.tags
      .addEntityTag(tag, 'SeasonLookup', this.value.id)
      .pipe(takeUntil(this.unSubSub$))
      .subscribe(
        (result) => {
          console.log(`${this._componentName}.onSelectTag addEntityTag subscription result=`, result);

          this.entityTagCloud.refresh();
        },
        (err) => {
          console.error(err);

          var msg = this.errorHelper.getErrorMessage(err) || 'A subscription error occured';

          this.saving = false;

          this.notify.error(msg, null);
        },
        () => {
          this.saving = false;
        }
      );
  }

  public onTagDelete(tag: TagViewModel) {
    this.tags
      .deleteEntityTag(tag.id, 'SeasonLookup', this.value.id)
      .pipe(takeUntil(this.unSubSub$))
      .subscribe(
        (result) => {
          console.log(`${this._componentName}.onTagDelete deleteEntityTag subscription result=`, result);

          this.entityTagCloud.refresh();
        },
        (err) => {
          console.error(err);

          var msg = this.errorHelper.getErrorMessage(err) || 'A subscription error occured';

          this.saving = false;

          this.notify.error(msg, null);
        },
        () => {
          this.saving = false;
        }
      );
  }
}
